import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SubLayout from "../../components/layout/SubLayout";
import MobileHeader from "../../components/layout/MobileHeader";
import ReportsList from "./ReportsList";
import CreateReport, { mapSavedReportToConfig } from "./CreateReport";
import PreviewReport from "./PreviewReport";
import API_MANAGER from "../../API";
import { showReportError } from "./reportToast";
import { ensureSocketConnected, waitForSocketConnected } from "../../components/Socket/socket";
import "../../styles/ReportBuilder/index.scss";

function ReportBuilder() {
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get("view");

  // "preview" cannot be restored on reload because its rows come from the API
  // response held in memory, so it falls back to the builder.
  const [view, setView] = useState(
    viewParam === "create" || viewParam === "preview" ? "create" : "list"
  );
  const [previewData, setPreviewData] = useState(null);
  const [previewConfig, setPreviewConfig] = useState(null);
  const [savedReportConfig, setSavedReportConfig] = useState(null);
  const [openingReportId, setOpeningReportId] = useState(null);
  const [reportId, setReportId] = useState(null);

  useEffect(() => {
    if (viewParam === "preview") {
      setSearchParams({ view: "create" }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    ensureSocketConnected();
    waitForSocketConnected().catch(() => {});
  }, []);

  const goToView = useCallback(
    (next) => {
      setView(next);
      if (next === "list") {
        setSearchParams({}, { replace: true });
      } else {
        setSearchParams({ view: next }, { replace: true });
      }
    },
    [setSearchParams]
  );

  const handleNewReport = useCallback(() => {
    setSavedReportConfig(null);
    setReportId(null);
    goToView("create");
  }, [goToView]);

  const handleBackFromCreate = useCallback(() => {
    setSavedReportConfig(null);
    setReportId(null);
    goToView("list");
  }, [goToView]);

  const handleOpenReport = useCallback(
    async (id) => {
      if (!id || openingReportId) return;

      setOpeningReportId(id);
      try {
        const response = await API_MANAGER.getReportDetails(id);
        const body = response?.data?.data;
        const report = body?.data || body?.report || body;

        const config = mapSavedReportToConfig(report);
        setSavedReportConfig(config);
        setReportId(config.reportId);
        goToView("create");
        waitForSocketConnected().catch(() => {});
      } catch (error) {
        showReportError(error, "Failed to open report");
      }
      setOpeningReportId(null);
    },
    [goToView, openingReportId]
  );

  const handlePreviewSuccess = useCallback(
    (data, config) => {
      setPreviewData(data);
      setPreviewConfig(config);
      setSavedReportConfig({
        reportTitle: config.reportTitle,
        selectedFields: (config.selectedFields || [])
          .map((field) => (typeof field === "string" ? field : field?.key || field?.id || ""))
          .filter(Boolean),
        filters: config.filters,
      });
      goToView("preview");
    },
    [goToView]
  );

  const handleBackFromPreview = useCallback(() => {
    setSavedReportConfig((prev) => {
      const fields = (previewConfig?.selectedFields || prev?.selectedFields || [])
        .map((field) =>
          typeof field === "string" ? field : field?.key || field?.id || ""
        )
        .filter(Boolean);
      return {
        reportTitle: previewConfig?.reportTitle || prev?.reportTitle || "Untitled report",
        selectedFields: fields,
        filters: previewConfig?.filters || prev?.filters || [],
      };
    });
    goToView("create");
  }, [goToView, previewConfig]);

  const handleSaveReportConfig = useCallback((config) => {
    setSavedReportConfig((prev) => {
      if (
        prev &&
        prev.reportTitle === config.reportTitle &&
        prev.selectedFields === config.selectedFields &&
        prev.filters === config.filters
      ) {
        return prev;
      }
      return config;
    });
  }, []);

  const handleReportSaved = useCallback((savedId) => {
    if (savedId) setReportId(savedId);
  }, []);

  const handlePreviewUpdate = useCallback(
    (nextRows, requestedColumns, nextTotalRows, displayColumns) => {
      const nextFields = Array.isArray(requestedColumns)
        ? requestedColumns
        : null;

      setPreviewData(nextRows);
      setPreviewConfig((prev) =>
        prev
          ? {
              ...prev,
              selectedFields: nextFields || prev.selectedFields,
              previewColumns: displayColumns || prev.previewColumns,
              totalRows: nextTotalRows,
            }
          : prev
      );
      setSavedReportConfig((prev) =>
        prev
          ? {
              ...prev,
              selectedFields: nextFields || prev.selectedFields,
            }
          : prev
      );
    },
    []
  );

  const handlePreviewTitleChange = useCallback((nextTitle) => {
    setPreviewConfig((prev) => (prev ? { ...prev, reportTitle: nextTitle } : prev));
    setSavedReportConfig((prev) => (prev ? { ...prev, reportTitle: nextTitle } : prev));
  }, []);

  return (
    <SubLayout
      page={"Reports"}
      showSearch
      showBack={view !== "list"}
      isBackFunc={view !== "list"}
      backUrlFunction={
        view === "preview" ? handleBackFromPreview : handleBackFromCreate
      }
    >
      <MobileHeader title={"Reports"} showSearch />

      <div className="report-builder-page">
        <div
          className={`report-builder-container ${
            view === "create" || view === "preview" ? "create-mode" : ""
          }`}
        >
          {view === "preview" ? (
            <PreviewReport
              reportTitle={previewConfig?.reportTitle}
              previewData={previewData}
              selectedFields={previewConfig?.selectedFields}
              apiColumns={previewConfig?.previewColumns}
              filters={previewConfig?.filters}
              filterPayload={previewConfig?.filterPayload}
              filterSummary={previewConfig?.filterSummary}
              totalRows={previewConfig?.totalRows}
              loadOnMount={Boolean(previewConfig?.loadOnMount)}
              onTitleChange={handlePreviewTitleChange}
              reportId={reportId}
              onSaved={handleReportSaved}
              onPreviewUpdate={handlePreviewUpdate}
            />
          ) : view === "create" ? (
            <CreateReport
              onBack={handleBackFromCreate}
              onPreviewSuccess={handlePreviewSuccess}
              initialConfig={savedReportConfig}
              onConfigChange={handleSaveReportConfig}
              reportId={reportId}
              onSaved={handleReportSaved}
            />
          ) : (
            <ReportsList
              onNewReport={handleNewReport}
              onOpenReport={handleOpenReport}
              openingReportId={openingReportId}
            />
          )}
        </div>
      </div>
    </SubLayout>
  );
}

export default ReportBuilder;
